import type { ReactNode } from 'react';
import { previewPeopleLines, type ImportPreviewGroup, type ImportPreviewPeople } from '@nest-recruit/shared';
import { MoreOptions } from '../../../components/MoreOptions';
import { toneClass, type StatusTone } from '../../../components/Tone';
import { formatNumber } from '../../../lib/format';
import styles from './PreviewPeople.module.css';

// Workflow review D3: IMP-01's preview says who the file brings, as the import will treat them. Someone already in the
// candidate bank is not a duplicate on this job: they are added to it and linked to their profile. Only someone who
// already applied to this job, or a second row for the same person, is skipped. Each group's names fold away.

interface PreviewPeopleProps {
  readonly people: ImportPreviewPeople;
}

export function PreviewPeople({ people }: PreviewPeopleProps): ReactNode {
  const lines = previewPeopleLines(people);
  return (
    <ul className={styles.groups} aria-label="Who this file brings">
      {lines.inBank && <Group tone="progress" text={lines.inBank} group={people.inBank} />}
      {lines.onThisJob && <Group tone="hold" text={lines.onThisJob} group={people.onThisJob} />}
      {lines.repeated && <Group tone="hold" text={lines.repeated} group={people.repeated} />}
      <li className={`${toneClass} ${styles.group}`} data-tone="new">
        <span className={styles.line}>
          <span className={styles.dot} aria-hidden />
          {lines.newPeople}
        </span>
      </li>
    </ul>
  );
}

function Group({ tone, text, group }: { readonly tone: StatusTone; readonly text: string; readonly group: ImportPreviewGroup }): ReactNode {
  const more = group.count - group.people.length;
  return (
    <li className={`${toneClass} ${styles.group}`} data-tone={tone}>
      <span className={styles.line}>
        <span className={styles.dot} aria-hidden />
        {text}
      </span>
      <MoreOptions label={`Show who (${formatNumber(group.count)})`}>
        <ol className={styles.names}>
          {group.people.map((person) => (
            <li key={person.rowNumber} className={styles.name}>
              <span className={styles.rowNumber}>Row {formatNumber(person.rowNumber)}</span>
              {person.name}
            </li>
          ))}
        </ol>
        {more > 0 && <p className={styles.more}>And {formatNumber(more)} more.</p>}
      </MoreOptions>
    </li>
  );
}
